import { useState, useCallback, useRef } from 'react';
import { stringify } from 'query-string';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { isCustomFilter } from '@atlassian/jira-issue-navigator-actions-common/src/utils/filters/index.tsx';
import {
	fireOperationalAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type {
	UseFetchAllExportIssuesResources,
	RequestObject,
	ExportItemsResponse,
	LastUsedJql,
} from '../../common/types.tsx';

export const EXPORT_ITEMS_ENDPOINT = '/rest/issueNav/1/issueNav/operations/views';

export const fetchExportItems = (body: RequestObject): Promise<ExportItemsResponse> =>
	performPostRequest(EXPORT_ITEMS_ENDPOINT, {
		credentials: 'same-origin',
		body: stringify(body),
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
			'X-Atlassian-Token': 'no-check',
			__amdModuleName: 'jira/issue/utils/xsrf-token-header',
		},
	});

export const useFetchAllExportIssuesResources = (
	jql: string,
	filterId: string | null | undefined,
	isFilterModified = false,
): UseFetchAllExportIssuesResources => {
	const [exportItems, setExportItems] = useState<ExportItemsResponse>();
	const [error, setError] = useState<Error>();
	const [loading, setLoading] = useState(true);
	const lastUsedJqlRef = useRef<LastUsedJql>();
	const lastUsedFilterIdRef = useRef<string | null | undefined>();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fetch = useCallback(
		async (fetchRetry: boolean) => {
			try {
				/** We don't want to refetch if the JQL and the filterID haven't changed.
				 * Our WRM resource doesn't depend on JQL however, asyncWRMRequire will
				 * not refetch a resource if it's already available.
				 */
				if (jql !== lastUsedJqlRef.current || filterId !== lastUsedFilterIdRef.current) {
					setLoading(true);

					const exportItemsResponse: ExportItemsResponse = await fetchExportItems({
						jql,
						getViewOptionHeader: true,
						...(filterId != null && isCustomFilter(filterId)
							? { filterId }
							: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								({} as Partial<RequestObject>)),
						...(isFilterModified
							? { modified: isFilterModified }
							: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
								({} as Partial<RequestObject>)),
					});

					lastUsedJqlRef.current = jql;
					lastUsedFilterIdRef.current = filterId;
					setExportItems(exportItemsResponse);
					setError(undefined);

					fireOperationalAnalytics(
						createAnalyticsEvent({
							type: 'sendOperationalEvent',
							action: 'success',
							actionSubject: 'fetchExportIssues',
						}),
						'fetchExportIssues success',
						{
							fetchRetry,
						},
					);
				}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (err: any) {
				log.safeErrorWithoutCustomerData(
					'export-issues.useFetchAllExportIssuesResources',
					'fetch resources',
					{
						shouldFetchExportIssuesResourcesOnClick: true,
						...err,
					},
				);
				fireErrorAnalytics({
					error: err,
					meta: {
						id: 'fetchExportIssues',
						packageName: 'jiraIssueNavigatorActionExportIssues',
						teamName: 'empanada',
					},
					attributes: {
						fetchRetry,
					},
				});
				setError(err);
			} finally {
				setLoading(false);
			}
		},
		[createAnalyticsEvent, filterId, jql, isFilterModified],
	);

	return {
		fetch,
		exportItems,
		loading,
		error,
	};
};

export default useFetchAllExportIssuesResources;
